/**
 * Les toasts se posent au-dessus des barres collées en bas d'écran (lot G5b).
 *
 * Sur téléphone, la barre de navigation de l'application, le pied d'actions
 * du recueil client ou du questionnaire restent collés en bas : un toast
 * sonner (16 px du bord) les recouvrait. Toute barre de ce genre porte
 * l'attribut `data-toast-clearance` ; on mesure la plus haute de celles qui
 * touchent effectivement le bas de l'écran et l'on expose la hauteur à
 * dégager dans `--toast-offset-bottom` (lu par le `<Toaster>` d'`App.tsx`).
 * Rien en bas (bureau, barre masquée, pied pas encore collé) : la variable
 * est retirée, sonner garde ses décalages par défaut.
 */

import { useEffect } from "react";

export const TOAST_CLEARANCE_VAR = "--toast-offset-bottom";

/** Espace entre la barre et le toast. */
const GAP_PX = 12;

/**
 * Hauteur à dégager au-dessus des barres collées en bas, 0 s'il n'y en a pas.
 * Les barres s'empilent (le bouton « Créer » du nouveau client au-dessus de
 * la navigation) : une barre compte si elle touche le bas de l'écran ou le
 * haut d'une barre déjà comptée.
 */
export function bottomClearance(
  bars: Iterable<Pick<HTMLElement, "getBoundingClientRect">>,
  viewportHeight: number,
): number {
  const rects = [...bars]
    .map((bar) => bar.getBoundingClientRect())
    .filter((rect) => rect.height > 0 && rect.width > 0) // masquée (lg:hidden…)
    .sort((a, b) => b.bottom - a.bottom);
  let clearance = 0;
  for (const rect of rects) {
    if (rect.bottom < viewportHeight - clearance - 1) continue; // pas collée en bas
    clearance = Math.max(clearance, viewportHeight - rect.top);
  }
  return clearance > 0 ? Math.ceil(clearance + GAP_PX) : 0;
}

function apply(): void {
  const bars = document.querySelectorAll<HTMLElement>("[data-toast-clearance]");
  const clearance = bottomClearance(bars, window.innerHeight);
  const root = document.documentElement;
  if (clearance > 0) root.style.setProperty(TOAST_CLEARANCE_VAR, `${clearance}px`);
  else root.style.removeProperty(TOAST_CLEARANCE_VAR);
}

/** Tient `--toast-offset-bottom` à jour (montage, redimensionnement, défilement, DOM). */
export function useToastClearance(): void {
  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };
    schedule();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { capture: true, passive: true });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, { capture: true });
      document.documentElement.style.removeProperty(TOAST_CLEARANCE_VAR);
    };
  }, []);
}
